import { Listbox, Transition } from "@headlessui/react";
import { find } from "lodash";
import React, { useEffect, useMemo } from "react";
import classNames from "../utils/classNames";
import useDataPeriod, { DataPeriodKey } from "../utils/useDataPeriod";

interface Props {
  className?: string;
  showLifetimeOption?: boolean;
}

const DataPeriodSelector: React.FC<Props> = ({
  className = "",
  showLifetimeOption = true
}) => {
  const dataPeriod = useDataPeriod((state) => state.dataPeriod);
  const dataPeriodOptions = useDataPeriod((state) =>
    state.getDataPeriodOptions()
  );
  const setDataPeriod = useDataPeriod((state) => state.setDataPeriod);

  const filteredDataPeriodOptions = useMemo(
    () =>
      dataPeriodOptions.filter((option) =>
        !showLifetimeOption ? option.key !== DataPeriodKey.lifetime : true
      ),
    [dataPeriodOptions, showLifetimeOption]
  );

  useEffect(() => {
    if (dataPeriod.key === DataPeriodKey.lifetime && !showLifetimeOption) {
      setDataPeriod(DataPeriodKey.currentMonth);
    }
  }, [dataPeriod.key, setDataPeriod, showLifetimeOption]);

  const currentDataPeriodTitle = useMemo(
    () => find(filteredDataPeriodOptions, { key: dataPeriod.key })?.title,
    [dataPeriod.key, filteredDataPeriodOptions]
  );

  return (
    <div className={className}>
      <Listbox
        as="div"
        className="space-y-1"
        value={dataPeriod.key}
        onChange={setDataPeriod}
      >
        {({ open }) => (
          <>
            <div className="relative">
              <span className="inline-block min-w-max rounded-md shadow-sm">
                <Listbox.Button className="cursor-default relative min-w-max rounded-md border border-gray-300 bg-white pl-3 pr-10 py-2 text-left focus:outline-none focus:shadow-outline-purple focus:border-hotspotty-300 transition ease-in-out duration-150 sm:text-sm sm:leading-5 dark:bg-gray-800 dark:text-white">
                  <span className="block truncate">
                    {currentDataPeriodTitle}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </Listbox.Button>
              </span>

              <Transition
                show={open}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                className="absolute bottom-10 mt-1 min-w-max rounded-md bg-white dark:bg-gray-800 shadow-lg"
              >
                <Listbox.Options
                  static
                  className="max-h-60 rounded-md py-1 text-base leading-6 shadow-xs overflow-auto focus:outline-none sm:text-sm sm:leading-5"
                >
                  {filteredDataPeriodOptions.map(({ key, title }) => (
                    <Listbox.Option key={key} value={key}>
                      {({ selected, active, disabled }) => (
                        <div
                          className={classNames(
                            active
                              ? "text-white bg-hotspotty-400 dark:bg-gray-700 dark:text-white"
                              : "text-gray-900 dark:bg-gray-800 dark:text-white",
                            disabled && "opacity-50",
                            "cursor-default select-none relative py-2 pl-8 pr-4"
                          )}
                        >
                          <span
                            className={classNames(
                              "block truncate",
                              selected ? "font-semibold" : "font-normal"
                            )}
                          >
                            {title}
                          </span>
                          {selected && (
                            <span
                              className={classNames(
                                "absolute inset-y-0 left-0 flex items-center pl-1.5",
                                active
                                  ? "text-white"
                                  : "text-hotspotty-500 dark:text-white"
                              )}
                            >
                              <svg
                                className="h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                          )}
                        </div>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  );
};

export default DataPeriodSelector;
